import { useState, useEffect, useMemo } from "react";
import { Category } from "@/types";
import { UnifiedCategoryService } from "@/services/UnifiedCategoryService";

export interface CategoryTab {
  id: number | string;
  label: string;
  color: string;
  icon?: React.ReactNode;
  slug?: string;
  order?: number;
}

interface UseCategoriesOptions {
  includeHome?: boolean;
  enableNewsCount?: boolean;
  locationSlug?: string | null;
  sortBy?: "order" | "name" | "id";
}

interface UseCategoriesReturn {
  categories: Category[];
  categoryTabs: CategoryTab[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Shared custom hook for category management
 * Used by both HomeHeader sidebar and Homepage CategoryTabs
 * Ensures consistent category logic across components
 */
export const useCategories = (
  options: UseCategoriesOptions = {},
): UseCategoriesReturn => {
  const {
    includeHome = true,
    enableNewsCount = false,
    locationSlug = null,
    sortBy = "order",
  } = options;

  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch categories function
  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Try to use UnifiedCategoryService first, fallback to direct API call
      let fetchedCategories: Category[] = [];

      try {
        const categoryData =
          await UnifiedCategoryService.fetchCategoriesWithConfig();
        fetchedCategories = categoryData.categories || [];
      } catch (serviceError) {
        console.warn(
          "UnifiedCategoryService failed, using fallback API call:",
          serviceError,
        );

        // Fallback to direct API v1 call
        const response = await fetch("/api/v1/categories");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        fetchedCategories = result.data || [];
      }

      setCategories(fetchedCategories);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch categories",
      );

      // Set empty array on error to prevent crashes
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch categories on mount and when dependencies change
  useEffect(() => {
    fetchCategories();
  }, [locationSlug, enableNewsCount]);

  // Transform categories to CategoryTabs with consistent logic
  const categoryTabs = useMemo(() => {
    if (!categories.length) {
      return includeHome
        ? [
            {
              id: "home",
              label: "Beranda",
              color: "red",
              slug: undefined,
              order: 0,
            },
          ]
        : [];
    }

    // Filter out "Hot News" category (same logic as Homepage)
    const filteredCategories = categories.filter(
      (cat) =>
        cat.name.toLowerCase() !== "hot news" &&
        cat.slug !== "hot-news" &&
        cat.slug !== "hot_news",
    );

    // Sort categories based on sortBy option
    const sortedCategories = [...filteredCategories].sort((a, b) => {
      switch (sortBy) {
        case "order":
          // Sort by order field first, then by name as fallback (same as Homepage)
          const orderA = a.order ?? 999;
          const orderB = b.order ?? 999;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return a.name.localeCompare(b.name);

        case "name":
          return a.name.localeCompare(b.name);

        case "id":
          return (a.id || 0) - (b.id || 0);

        default:
          return 0;
      }
    });

    // Transform to CategoryTab format
    const transformedTabs: CategoryTab[] = sortedCategories.map((cat) => ({
      id: cat.id || cat.slug || "",
      label: cat.name,
      color: "red", // Default color since Category type doesn't have color property
      slug: cat.slug,
      order: cat.order,
      // Add news count if enabled (placeholder for future implementation)
      ...(enableNewsCount && { newsCount: cat.news_count || 0 }),
    }));

    // Add "Beranda" tab at the beginning if requested
    if (includeHome) {
      transformedTabs.unshift({
        id: "home",
        label: "Beranda",
        color: "red",
        slug: undefined,
        order: 0,
      });
    }

    return transformedTabs;
  }, [categories, includeHome, enableNewsCount, sortBy]);

  // Refetch function for manual refresh
  const refetch = async () => {
    await fetchCategories();
  };

  return {
    categories,
    categoryTabs,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook variant specifically for sidebar usage
 * Pre-configured with sidebar-appropriate defaults
 */
export const useSidebarCategories = (locationSlug?: string | null) => {
  return useCategories({
    includeHome: true,
    enableNewsCount: false,
    locationSlug,
    sortBy: "order",
  });
};

/**
 * Hook variant specifically for homepage tabs usage
 * Pre-configured with homepage-appropriate defaults
 */
export const useHomepageCategories = (locationSlug?: string | null) => {
  return useCategories({
    includeHome: true,
    enableNewsCount: false,
    locationSlug,
    sortBy: "order",
  });
};

/**
 * Hook for categories without home tab
 * Useful for category-only listings
 */
export const useCategoriesOnly = (locationSlug?: string | null) => {
  return useCategories({
    includeHome: false,
    enableNewsCount: false,
    locationSlug,
    sortBy: "order",
  });
};

export default useCategories;
